/**
 * MessageFormModal - Модальное окно для создания/редактирования сообщения
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  Button,
  Group,
  Stack,
  Alert,
  Divider,
  SegmentedControl,
  Text,
} from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import {
  IconAlertCircle,
  IconCalendar,
  IconClock,
  IconBrandWhatsapp,
  IconBrandTelegram,
  IconBrandVk,
  IconPhone,
  IconMail,
} from '@tabler/icons-react';
import { useSession } from '../../../context/SessionContext';

/**
 * Конфигурация мессенджеров
 */
const MESSENGER_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp', icon: IconBrandWhatsapp },
  { value: 'telegram', label: 'Telegram', icon: IconBrandTelegram },
  { value: 'viber', label: 'Viber', icon: IconPhone },
  { value: 'vk', label: 'VK', icon: IconBrandVk },
  { value: 'email', label: 'Email', icon: IconMail },
];

/**
 * Типы сообщений
 */
const MESSAGE_TYPE_OPTIONS = [
  { value: 'primary', label: 'Первичное' },
  { value: 'secondary', label: 'Вторичное' },
];

/**
 * Получить placeholder для контакта в зависимости от мессенджера
 */
const getContactPlaceholder = (messengerType) => {
  switch (messengerType) {
    case 'whatsapp':
    case 'viber':
      return '+7 (___) ___-__-__';
    case 'telegram':
      return '@username или +7...';
    case 'vk':
      return 'vk.com/id или @username';
    case 'email':
      return 'example@domain.com';
    default:
      return 'Контакт';
  }
};

/**
 * Получить label для контакта
 */
const getContactLabel = (messengerType) => {
  switch (messengerType) {
    case 'whatsapp':
    case 'viber':
      return 'Номер телефона';
    case 'telegram':
      return 'Username или телефон';
    case 'vk':
      return 'ID или ссылка';
    case 'email':
      return 'Email адрес';
    default:
      return 'Контакт';
  }
};

/**
 * Форматирование телефона
 */
const formatPhone = (value) => {
  if (!value) return '';

  // Удаляем все кроме цифр
  const digits = value.replace(/\D/g, '');

  // Если начинается с 8, заменяем на 7
  let normalized = digits;
  if (normalized.startsWith('8') && normalized.length > 1) {
    normalized = '7' + normalized.slice(1);
  }

  // Форматируем
  if (normalized.length === 0) return '';
  if (normalized.length <= 1) return `+${normalized}`;
  if (normalized.length <= 4) return `+${normalized.slice(0, 1)} (${normalized.slice(1)}`;
  if (normalized.length <= 7) return `+${normalized.slice(0, 1)} (${normalized.slice(1, 4)}) ${normalized.slice(4)}`;
  if (normalized.length <= 9) return `+${normalized.slice(0, 1)} (${normalized.slice(1, 4)}) ${normalized.slice(4, 7)}-${normalized.slice(7)}`;
  return `+${normalized.slice(0, 1)} (${normalized.slice(1, 4)}) ${normalized.slice(4, 7)}-${normalized.slice(7, 9)}-${normalized.slice(9, 11)}`;
};

/**
 * Форматирование имени пользователя
 */
const formatUserName = (user) => {
  if (user.full_name) return user.full_name;
  const parts = [];
  if (user.last_name) parts.push(user.last_name);
  if (user.first_name) parts.push(user.first_name);
  return parts.join(' ') || user.email;
};

const MessageFormModal = ({
  opened,
  onClose,
  onSubmit,
  message = null,
  organizationId,
  loading = false,
  getTemplate, // Функция для загрузки шаблона
}) => {
  const { user: currentUser } = useSession();
  const isEditing = !!message;

  const [formData, setFormData] = useState({
    messenger_type: 'whatsapp',
    messenger_contact: '',
    message_type: 'primary',
    message_text: '',
    sender_user_id: null,
    sent_date: new Date(),
    sent_time: '',
  });
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  // Загрузка списка пользователей
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const response = await fetch('/api/users/sales-managers', {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setUsers(data.items || []);
        }
      } catch (err) {
        console.error('Failed to load users:', err);
      } finally {
        setLoadingUsers(false);
      }
    };

    if (opened) {
      fetchUsers();
    }
  }, [opened]);

  // Форматирование времени из даты
  const formatTimeFromDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Инициализация формы при открытии
  useEffect(() => {
    if (opened) {
      if (message) {
        // Редактирование
        const sentDate = new Date(message.sent_datetime);
        setFormData({
          messenger_type: message.messenger_type || 'whatsapp',
          messenger_contact: message.messenger_contact || '',
          message_type: message.message_type || 'primary',
          message_text: message.message_text || '',
          sender_user_id: message.sender_user_id?.toString() || null,
          sent_date: sentDate,
          sent_time: formatTimeFromDate(sentDate),
        });
      } else {
        // Создание
        const now = new Date();
        setFormData({
          messenger_type: 'whatsapp',
          messenger_contact: '',
          message_type: 'primary',
          message_text: '',
          sender_user_id: currentUser?.id?.toString() || null,
          sent_date: now,
          sent_time: formatTimeFromDate(now),
        });
      }
      setError(null);
    }
  }, [opened, message, currentUser]);

  // Загрузка шаблона при смене типа сообщения на "primary"
  useEffect(() => {
    const loadTemplate = async () => {
      if (!opened || isEditing) return;
      if (formData.message_type !== 'primary') return;
      if (formData.message_text) return; // Не перезаписываем если уже есть текст
      if (!getTemplate) return;

      try {
        setLoadingTemplate(true);
        const template = await getTemplate(formData.messenger_type);
        if (template && template.message_text) {
          setFormData(prev => ({
            ...prev,
            message_text: template.message_text,
          }));
        }
      } catch (err) {
        console.error('Failed to load template:', err);
      } finally {
        setLoadingTemplate(false);
      }
    };

    loadTemplate();
  }, [opened, isEditing, formData.messenger_type, formData.message_type, getTemplate]);

  // Подготовка данных для Select пользователей
  const userOptions = users.map((user) => ({
    value: user.id.toString(),
    label: formatUserName(user),
  }));

  // Обработка изменения контакта (форматирование телефона)
  const handleContactChange = (value) => {
    let formattedValue = value;

    // Для телефонных мессенджеров форматируем как телефон
    if (['whatsapp', 'viber'].includes(formData.messenger_type)) {
      formattedValue = formatPhone(value);
    }

    setFormData({ ...formData, messenger_contact: formattedValue });
  };

  // Обработка изменения мессенджера
  const handleMessengerChange = (value) => {
    setFormData({
      ...formData,
      messenger_type: value,
      messenger_contact: '', // Сбрасываем контакт при смене мессенджера
      message_text: '', // Сбрасываем текст для загрузки нового шаблона
    });
  };

  // Обработка изменения типа сообщения
  const handleMessageTypeChange = (value) => {
    setFormData({
      ...formData,
      message_type: value,
      message_text: value === 'secondary' ? '' : formData.message_text,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Валидация
    if (!formData.messenger_contact?.trim()) {
      setError('Укажите контакт');
      return;
    }

    if (!formData.sent_date) {
      setError('Укажите дату отправки');
      return;
    }

    if (!formData.sent_time) {
      setError('Укажите время отправки');
      return;
    }

    if (!formData.message_text?.trim()) {
      setError('Введите текст сообщения');
      return;
    }

    try {
      // Собираем дату и время
      const [hours, minutes] = formData.sent_time.split(':').map(Number);
      const sentDatetime = new Date(formData.sent_date);
      sentDatetime.setHours(hours || 0, minutes || 0, 0, 0);

      const data = {
        organization_id: organizationId,
        messenger_type: formData.messenger_type,
        messenger_contact: formData.messenger_contact.trim(),
        message_type: formData.message_type,
        message_text: formData.message_text.trim(),
        sender_user_id: formData.sender_user_id ? parseInt(formData.sender_user_id, 10) : null,
        sent_datetime: sentDatetime.toISOString(),
      };

      await onSubmit(data);
    } catch (err) {
      setError(err.message || 'Ошибка при сохранении сообщения');
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditing ? 'Редактировать сообщение' : 'Добавить сообщение'}
      size="lg"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}

          {/* Мессенджер */}
          <Select
            label="Мессенджер"
            placeholder="Выберите мессенджер"
            required
            data={MESSENGER_OPTIONS}
            value={formData.messenger_type}
            onChange={handleMessengerChange}
            allowDeselect={false}
          />

          {/* Контакт */}
          <TextInput
            label={getContactLabel(formData.messenger_type)}
            placeholder={getContactPlaceholder(formData.messenger_type)}
            required
            value={formData.messenger_contact}
            onChange={(e) => handleContactChange(e.target.value)}
          />

          {/* Тип сообщения */}
          <div>
            <Text size="sm" fw={500} mb={4}>
              Тип сообщения
            </Text>
            <SegmentedControl
              value={formData.message_type}
              onChange={handleMessageTypeChange}
              data={MESSAGE_TYPE_OPTIONS}
              fullWidth
              radius="xl"
              color={formData.message_type === 'primary' ? 'green' : 'yellow'}
            />
          </div>

          {/* Отправитель */}
          <Select
            label="Кто отправил"
            placeholder="Выберите сотрудника"
            data={userOptions}
            value={formData.sender_user_id}
            onChange={(value) => setFormData({ ...formData, sender_user_id: value })}
            searchable
            clearable
            nothingFoundMessage={loadingUsers ? 'Загрузка...' : 'Сотрудники не найдены'}
          />

          <Divider label="Дата и время" labelPosition="left" />

          {/* Дата и время */}
          <Group grow>
            <DatePickerInput
              label="Дата"
              placeholder="Выберите дату"
              required
              value={formData.sent_date}
              onChange={(value) => setFormData({ ...formData, sent_date: value })}
              leftSection={<IconCalendar size={16} />}
              valueFormat="DD.MM.YYYY"
              locale="ru"
            />
            <TimeInput
              label="Время"
              placeholder="00:00"
              required
              value={formData.sent_time}
              onChange={(e) => setFormData({ ...formData, sent_time: e.target.value })}
              leftSection={<IconClock size={16} />}
            />
          </Group>

          {/* Текст сообщения */}
          <Textarea
            label="Текст сообщения"
            placeholder={loadingTemplate ? 'Загрузка шаблона...' : 'Текст отправленного сообщения...'}
            required
            autosize
            minRows={6}
            value={formData.message_text}
            onChange={(e) => setFormData({ ...formData, message_text: e.target.value })}
            disabled={loadingTemplate}
          />

          {/* Кнопки */}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" loading={loading}>
              {isEditing ? 'Сохранить' : 'Добавить'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default MessageFormModal;
